import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Mail, Lock, User, CreditCard, Phone, MapPinned, Globe, Eye, EyeOff, ShoppingCart, Package, ArrowLeftRight, MessageCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import countries from '../data/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RegisterPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    company_name: '',
    company_description: '',
    phone: '',
    address: '',
    country: '',
    account_type: 'both'
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { register } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  // OTP states
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpCountdown, setOtpCountdown] = useState(0);

  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  // Reset OTP state when phone changes
  const handlePhoneChange = (e) => {
    const newPhone = e.target.value;
    setFormData({ ...formData, phone: newPhone });
    if (phoneVerified) {
      setPhoneVerified(false);
      setOtpSent(false);
      setOtpCode('');
    }
  };

  const sendOTP = async () => {
    if (!formData.phone || formData.phone.length < 8) {
      toast.error(language === 'ar' ? 'يرجى إدخال رقم هاتف صحيح' : 'Please enter a valid phone number');
      return;
    }
    setOtpLoading(true);
    try {
      const { data } = await axios.post(`${API}/otp/send`, { phone: formData.phone });
      setOtpSent(true);
      setOtpCountdown(60);
      toast.success(language === 'ar' ? 'تم إرسال رمز التحقق عبر واتساب' : 'OTP sent via WhatsApp');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      toast.error(detail || (language === 'ar' ? 'فشل إرسال رمز التحقق' : 'Failed to send OTP'));
    } finally {
      setOtpLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!otpCode || otpCode.length !== 6) {
      toast.error(language === 'ar' ? 'يرجى إدخال رمز مكون من 6 أرقام' : 'Please enter a 6-digit code');
      return;
    }
    setOtpLoading(true);
    try {
      await axios.post(`${API}/otp/verify`, { phone: formData.phone, code: otpCode });
      setPhoneVerified(true);
      toast.success(language === 'ar' ? 'تم التحقق من رقم الهاتف بنجاح' : 'Phone verified successfully');
    } catch (err) {
      toast.error(language === 'ar' ? 'رمز التحقق غير صحيح أو منتهي الصلاحية' : 'Invalid or expired OTP');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.phone && !phoneVerified) {
      setError(language === 'ar' ? 'يرجى التحقق من رقم الهاتف أولاً' : 'Please verify your phone number first');
      return;
    }

    setLoading(true);
    const result = await register(formData);
    
    if (result.success) {
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 py-12 px-6" data-testid="register-page">
      <div className="max-w-2xl mx-auto">
        {/* Language Toggle */}
        <div className="flex justify-end mb-4">
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors bg-white rounded-lg border border-border"
            data-testid="language-toggle"
          >
            <Globe size={18} />
            {language === 'ar' ? 'English' : 'عربي'}
          </button>
        </div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-accent to-accent/70 rounded-xl flex items-center justify-center">
              <Building2 size={28} className="text-white" />
            </div>
            <span className="text-3xl font-heading font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{t('appName')}</span>
          </Link>
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">{t('createNewAccount')}</h1>
          <p className="text-muted-foreground">{t('joinBiggestPlatform')}</p>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-border shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('fullName')}
                </label>
                <div className="relative">
                  <User size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    data-testid="name-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder={language === 'ar' ? 'محمد أحمد' : 'John Doe'}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('email')}
                </label>
                <div className="relative">
                  <Mail size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="email-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="example@company.com"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('password')}
              </label>
              <div className="relative">
                <Lock size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  data-testid="password-input"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                  className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {/* Account Type Selection */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-3">
                {language === 'ar' ? 'نوع الحساب' : 'Account Type'} <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-3 gap-3">
                <button type="button" onClick={() => setFormData({...formData, account_type: 'seller'})} data-testid="account-type-seller"
                  className={`p-4 rounded-xl border-2 text-center transition-all ${formData.account_type === 'seller' ? 'border-accent bg-accent/5 shadow-sm' : 'border-border hover:border-accent/40'}`}>
                  <Package size={28} className={`mx-auto mb-2 ${formData.account_type === 'seller' ? 'text-accent' : 'text-muted-foreground'}`} />
                  <p className={`text-sm font-semibold ${formData.account_type === 'seller' ? 'text-accent' : 'text-primary'}`}>
                    {language === 'ar' ? 'بائع' : 'Seller'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {language === 'ar' ? 'نشر عروض البيع' : 'Post sale offers'}
                  </p>
                </button>
                <button type="button" onClick={() => setFormData({...formData, account_type: 'buyer'})} data-testid="account-type-buyer"
                  className={`p-4 rounded-xl border-2 text-center transition-all ${formData.account_type === 'buyer' ? 'border-green-500 bg-green-50 shadow-sm' : 'border-border hover:border-green-400'}`}>
                  <ShoppingCart size={28} className={`mx-auto mb-2 ${formData.account_type === 'buyer' ? 'text-green-600' : 'text-muted-foreground'}`} />
                  <p className={`text-sm font-semibold ${formData.account_type === 'buyer' ? 'text-green-600' : 'text-primary'}`}>
                    {language === 'ar' ? 'مشتري' : 'Buyer'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {language === 'ar' ? 'نشر طلبات الشراء' : 'Post buy requests'}
                  </p>
                </button>
                <button type="button" onClick={() => setFormData({...formData, account_type: 'both'})} data-testid="account-type-both"
                  className={`p-4 rounded-xl border-2 text-center transition-all ${formData.account_type === 'both' ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-border hover:border-purple-400'}`}>
                  <ArrowLeftRight size={28} className={`mx-auto mb-2 ${formData.account_type === 'both' ? 'text-purple-600' : 'text-muted-foreground'}`} />
                  <p className={`text-sm font-semibold ${formData.account_type === 'both' ? 'text-purple-600' : 'text-primary'}`}>
                    {language === 'ar' ? 'بائع ومشتري' : 'Both'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    {language === 'ar' ? 'بيع وشراء' : 'Sell & Buy'}
                  </p>
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('companyName')}
              </label>
              <div className="relative">
                <CreditCard size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  data-testid="company-name-input"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                  placeholder={language === 'ar' ? 'شركة الابتكار للتقنية' : 'Innovation Tech Co.'}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('companyDescription')}
              </label>
              <textarea
                name="company_description"
                value={formData.company_description}
                onChange={handleChange}
                required
                data-testid="company-description-input"
                rows={3}
                className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent resize-none"
                placeholder={language === 'ar' ? 'وصف مختصر عن نشاط الشركة...' : 'Brief description of company activities...'}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'ar' ? 'رقم الهاتف' : 'Phone Number'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <Phone size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handlePhoneChange}
                    required
                    disabled={phoneVerified}
                    data-testid="phone-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border ${phoneVerified ? 'border-green-400 bg-green-50' : 'border-border'} rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="+96478XXXXXXX"
                  />
                  {phoneVerified && (
                    <CheckCircle size={20} className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-green-500`} />
                  )}
                </div>

                {/* OTP Section */}
                {!phoneVerified && formData.phone.length >= 8 && (
                  <div className="mt-2 space-y-2">
                    {!otpSent ? (
                      <button
                        type="button"
                        onClick={sendOTP}
                        disabled={otpLoading}
                        data-testid="send-otp-btn"
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50 transition-all"
                      >
                        <MessageCircle size={18} />
                        {otpLoading
                          ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                          : (language === 'ar' ? 'إرسال رمز التحقق عبر واتساب' : 'Send OTP via WhatsApp')}
                      </button>
                    ) : (
                      <>
                        <div className="flex gap-2">
                          <input
                            type="text"
                            value={otpCode}
                            onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                            data-testid="otp-code-input"
                            className="flex-1 px-4 py-2.5 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent text-center text-lg tracking-widest font-mono"
                            placeholder="000000"
                            maxLength={6}
                          />
                          <button
                            type="button"
                            onClick={verifyOTP}
                            disabled={otpLoading || otpCode.length !== 6}
                            data-testid="verify-otp-btn"
                            className="px-4 py-2.5 bg-accent text-white text-sm font-medium rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-all"
                          >
                            {otpLoading
                              ? (language === 'ar' ? 'تحقق...' : 'Verifying...')
                              : (language === 'ar' ? 'تحقق' : 'Verify')}
                          </button>
                        </div>
                        <button
                          type="button"
                          onClick={sendOTP}
                          disabled={otpCountdown > 0 || otpLoading}
                          data-testid="resend-otp-btn"
                          className="w-full text-sm text-muted-foreground hover:text-accent disabled:opacity-50 transition-colors"
                        >
                          {otpCountdown > 0
                            ? (language === 'ar' ? `إعادة الإرسال بعد ${otpCountdown} ثانية` : `Resend in ${otpCountdown}s`)
                            : (language === 'ar' ? 'إعادة إرسال الرمز' : 'Resend code')}
                        </button>
                      </>
                    )}
                  </div>
                )}
                {phoneVerified && (
                  <p className="mt-1 text-xs text-green-600 font-medium" data-testid="phone-verified-badge">
                    {language === 'ar' ? 'تم التحقق من رقم الهاتف' : 'Phone number verified'}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {t('addressOptional')}
                </label>
                <div className="relative">
                  <MapPinned size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    data-testid="address-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder={language === 'ar' ? 'بغداد، العراق' : 'Baghdad, Iraq'}
                  />
                </div>
              </div>
            </div>

            {/* Country Selector */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('country')} <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <Globe size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <select
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  data-testid="country-select"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent appearance-none bg-white`}
                >
                  <option value="">{t('selectCountry')}</option>
                  {countries.map(c => (
                    <option key={c.code} value={c.code}>
                      {c.flag} {language === 'ar' ? c.name_ar : c.name_en}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-testid="error-message">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
            >
              {loading ? t('registering') : t('createAccount')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {t('haveAccount')}{' '}
              <Link to="/login" className="text-accent hover:underline font-medium" data-testid="login-link-from-register">
                {t('login')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}